/**
 * Blog yazarları (E-E-A-T). Blog detay, /yazar/:slug sayfaları ve SSR JSON-LD bu veriyi kullanır.
 *
 * KURAL: Yalnızca gerçek ve doğrulanmış kişiler kişi olarak imza atar.
 * - `dogrulandi: false` olan kişilerin yazıları "Dijipo Uzman Ekibi" (kurum) imzasıyla yayınlanır,
 *   kişi sayfaları ekip sayfasına 301 ile yönlenir ve sitemap'e girmez.
 * - Gerçek bir ekip üyesini açmak için: ad, unvan, bio, gerçek portre fotoğrafı (ör. /yazarlar/ad-soyad.jpg),
 *   LinkedIn adresi ve yalnızca gerçekten alınmış sertifikaları girip `dogrulandi: true` yapın.
 * Uydurma kişi, profil veya sertifika Google'ın güven değerlendirmesinde sitenin tamamına zarar verir.
 */
import type { BlogPost } from "./mock_data";

export interface Yazar {
  slug: string;
  ad: string;
  tur: "kisi" | "ekip";
  unvan: string;
  bio: string;
  /** Kare portre fotoğrafı. Kişiler için gerçek fotoğraf olmalı. */
  foto: string;
  /** Gerçek ve doğrulanmış kişi mi? (ekip için her zaman true) */
  dogrulandi: boolean;
  /** Tam LinkedIn profil adresi; boşsa gösterilmez */
  linkedin?: string;
  /** Doğrulanabilir sertifikalar; boşsa gösterilmez */
  sertifikalar?: string[];
  uzmanlik: string[];
}

/** Kurum imzası: doğrulanmış kişi yoksa tüm yazılar bu imzayla yayınlanır */
export const EKIP: Yazar = {
  slug: "dijipo-uzman-ekibi",
  ad: "Dijipo Uzman Ekibi",
  tur: "ekip",
  unvan: "E-ticaret reklam, altyapı ve SEO ekibi",
  bio: "Dijipo'nun performans reklamı, Shopify ve ikas altyapısı, kreatif ve SEO/GEO ekiplerinin ortak imzası. Rehberler, müşteri projelerinde uyguladığımız yöntemlere ve platformların güncel dokümantasyonuna dayanır.",
  foto: "/dijipo-logo-160.png",
  dogrulandi: true,
  uzmanlik: ["Meta ve Google reklam yönetimi", "ikas ve Shopify kurulumu ve geçişi", "Pixel ve Conversions API ölçümlemesi", "SEO ve GEO", "Tesettür, kadın giyim ve butik markaları"],
};

/** Editoryal ilkeler (ekip sayfasında ve SSR'da gösterilir) — yalnızca uygulanan kurallar */
export const EDITORYAL_ILKELER: string[] = [
  "Rehberler, e-ticaret müşterilerimizin hesaplarında uyguladığımız yöntemlere ve Meta, Google, ikas ve Shopify'ın güncel dokümantasyonuna dayanır.",
  "Müşteri markalarının adını ve verisini izinsiz yayınlamayız; vaka analizlerindeki rakamlar anonimleştirilmiş gerçek verilerdir.",
  "Fiyat, mevzuat ve platform kuralı gibi değişebilen bilgilerde güncel kaynağı kontrol etmenizi açıkça not ederiz.",
  "Bir yazıyı kapsamlı biçimde güncellediğimizde yayın tarihinin yanına güncelleme tarihini yazarız.",
  "Hata veya güncel olmayan bilgi bildirimi için admin@dijipo.com adresine yazabilirsiniz; doğrulanan düzeltmeleri yazıya işleriz.",
];

/** Kişi yazarlar. Gerçek oldukları doğrulanana kadar dogrulandi: false. */
const KISILER: Yazar[] = [
  {
    slug: "caner-demir",
    ad: "Caner Demir",
    tur: "kisi",
    unvan: "Kurucu & E-Ticaret Stratejisti",
    bio: "Dijipo'da altyapı seçimi, pazaryeri stratejisi ve büyüme planlamasından sorumlu.",
    foto: "/avatars/kurucu.svg",
    dogrulandi: false,
    linkedin: "",
    sertifikalar: [],
    uzmanlik: ["E-ticaret büyüme stratejisi", "ikas ve Shopify altyapı seçimi", "Pazaryeri stratejisi"],
  },
  {
    slug: "selin-yilmaz",
    ad: "Selin Yılmaz",
    tur: "kisi",
    unvan: "Performans Pazarlama Direktörü",
    bio: "Meta ve Google reklam sistemlerinde e-ticaret kampanyalarını yönetiyor.",
    foto: "/avatars/pazarlama-direktoru.svg",
    dogrulandi: false,
    linkedin: "",
    sertifikalar: [],
    uzmanlik: ["Meta Ads", "Google Ads", "Pixel ve Conversions API"],
  },
  {
    slug: "emre-kaya",
    ad: "Emre Kaya",
    tur: "kisi",
    unvan: "ikas & Shopify Teknik Uzmanı",
    bio: "ikas ve Shopify mağazalarında tema, hız, ölçümleme ve geçişlerin teknik tarafını yürütüyor.",
    foto: "/avatars/eticaret-uzmani.svg",
    dogrulandi: false,
    linkedin: "",
    sertifikalar: [],
    uzmanlik: ["ikas ve Shopify tema geliştirme", "Core Web Vitals", "Merchant Center ve ürün feed'i"],
  },
];

/** Yayında görünen yazarlar: doğrulanmış kişiler + ekip */
export const YAZARLAR: Yazar[] = [...KISILER.filter(k => k.dogrulandi), EKIP];

/** Doğrulanmamış kişi sayfaları → ekip sayfası (301) */
export const YAZAR_YONLENDIRMELERI: Record<string, string> = Object.fromEntries(
  KISILER.filter(k => !k.dogrulandi).map(k => [`/yazar/${k.slug}`, `/yazar/${EKIP.slug}`])
);

/** Yazının görünen imzası: doğrulanmış kişi veya ekip */
export const yazarBul = (ad: string): Yazar => KISILER.find(k => k.ad === ad && k.dogrulandi) ?? EKIP;
export const yazarBySlug = (slug: string) => YAZARLAR.find(y => y.slug === slug);
export const yazarYazilari = (y: Yazar, hepsi: BlogPost[]) => hepsi.filter(p => yazarBul(p.author).slug === y.slug);

/** Person / Organization JSON-LD (yalnızca dolu alanlar) */
export function yazarLd(y: Yazar, site = "https://dijipo.com") {
  const image = y.foto.startsWith("http") ? y.foto : `${site}${y.foto}`;
  if (y.tur === "ekip") {
    return {
      "@type": "Organization",
      "@id": `${site}/yazar/${y.slug}#ekip`,
      name: y.ad,
      description: y.bio,
      url: `${site}/yazar/${y.slug}`,
      logo: image,
      knowsAbout: y.uzmanlik,
      parentOrganization: { "@type": "Organization", name: "Dijipo", url: site },
    };
  }
  const sameAs = [y.linkedin].filter((u): u is string => !!u);
  return {
    "@type": "Person",
    "@id": `${site}/yazar/${y.slug}#person`,
    name: y.ad,
    jobTitle: y.unvan,
    description: y.bio,
    image,
    url: `${site}/yazar/${y.slug}`,
    knowsAbout: y.uzmanlik,
    ...(sameAs.length ? { sameAs } : {}),
    ...(y.sertifikalar?.length ? { hasCredential: y.sertifikalar.map(s => ({ "@type": "EducationalOccupationalCredential", name: s })) } : {}),
    worksFor: { "@type": "Organization", name: "Dijipo", url: site },
  };
}
